import styled from "@emotion/styled";
import ons from "onsenui";
import React from "react";
import { mergeRefs } from "react-merge-refs";
import { baseInputCss, baseInputIosFixCss } from "./Question";
import QuestionWrapper from "./QuestionWrapper";

function QuestionDateInput(
  props: React.ComponentPropsWithRef<typeof QuestionDateInput.Styled> &
    JSX.IntrinsicElements["input"],
  outerRef: React.Ref<HTMLInputElement>
) {
  const innerRef = React.useRef<HTMLInputElement | null>(null);
  const ref = mergeRefs([outerRef, innerRef]);
  const isIOS = ons.platform.isIOS();

  // In Safari (IOS), we can't add a placeholder for input[type="date"],
  // Thus, we need to mimic this behavior with a workaround.
  const { isEmpty } = useUncontrolledIsEmptyInput({
    ref: innerRef,
    enabled: isIOS,
  });

  return (
    <QuestionWrapper>
      <QuestionDateInput.Root>
        <QuestionDateInput.Styled
          data-is-empty={isEmpty}
          data-is-ios={isIOS}
          type="date"
          role="textbox"
          aria-label={props.name}
          ref={ref}
          {...props}
        />
      </QuestionDateInput.Root>
    </QuestionWrapper>
  );
}

function useUncontrolledIsEmptyInput(params: {
  ref: React.RefObject<HTMLInputElement>;
  enabled: boolean;
}) {
  const [isEmpty, setIsEmpty] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const { current: input } = params.ref;

    if (input === null) {
      return;
    }

    setIsEmpty(input.value === "");

    const handleCallback = (e: Event) => {
      const { target: elem } = e;

      if (elem === null) {
        return;
      }

      setIsEmpty((elem as HTMLInputElement).value === "");
    };

    input.addEventListener("change", handleCallback);

    return () => input.removeEventListener("change", handleCallback);
  }, [params.ref]);

  return { isEmpty };
}

QuestionDateInput.Styled = styled.input`
  ${baseInputCss}
  ${baseInputIosFixCss}
`;

QuestionDateInput.Root = styled.div`
  display: flex;
`;

export default React.forwardRef(QuestionDateInput);
